import { screeningSummaryEligibility } from '../screeningSummary/eligibility';
import { REPORT_PDF_BUILDER_VERSION } from '../screeningSummary/vocabulary';
import { reportSummaryEligibility, reportSummaryViewForType, reportSummaryBuilderVersionForView } from '../reportSummary';
import type { ReportTypeId } from '../../config/reportTypes';
import { advisoryFilteredDeliveryDecision, canBuildReportArtifacts } from '../reportGeneration/evaluateReportDelivery';
import { hashCanonicalJson } from '../reportGeneration/reportContractHash';
import type { SiteRagReport } from '../../types/siteRag';
import {
  analysisReadinessDecisionV1Schema,
  analysisRevisionReferenceV1Schema,
  decisionAnalysisSnapshotV1Schema,
} from '../../types/decisionAnalysis';
import { verifyDecisionAnalysisSnapshotIdentity } from '../decisionAnalysis/identity';
import { canExportAnalysisForPrintableReview } from '../decisionAnalysis/readiness';
import { reportContentRevisionReferenceV1Schema } from '../../types/reportContent';
import { reportRevisionReferenceV1Schema } from '../../types/reportContractsV2';
import type {
  ReportArtifactFormat,
  ReportArtifactJobRequest,
  ReportArtifactPageSize,
} from '../../services/reportArtifactJobsClient';

export class ReportArtifactRequestError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ReportArtifactRequestError';
  }
}

function formatFields(
  format: ReportArtifactFormat,
  pageSize: ReportArtifactPageSize,
): { format: 'pdf'; pageSize: ReportArtifactPageSize }
  | { format: Exclude<ReportArtifactFormat, 'pdf'> } {
  return format === 'pdf' ? { format, pageSize } : { format };
}

/**
 * Bind a new export to the exact immutable source currently open in Findings.
 *
 * Analysis-backed readers must be current and decision-ready. Historical
 * artifacts stay downloadable elsewhere, but stale/held/failed analysis
 * revisions cannot be used to create new files. Pre-analysis V2 reports retain
 * their existing server delivery gate; legacy illustrative Financial reports
 * are intentionally excluded from that compatibility path.
 *
 * Refusal copy names the report page's own action ("Rerun"): the only caller
 * is SiteRagReportView's export actions, so the user is on that page.
 */
export function createReportArtifactRequest(
  report: SiteRagReport,
  format: ReportArtifactFormat,
  pageSize: ReportArtifactPageSize = 'A4',
  options: { view?: 'screening-summary' | 'report-summary'; summaryBuilderVersion?: string; pdfBuilderVersion?: string } = {},
): ReportArtifactJobRequest {
  if (report._metadataOnly === true) {
    throw new ReportArtifactRequestError(
      'Open the complete report revision before creating an export.',
    );
  }

  const revision = reportRevisionReferenceV1Schema.safeParse(report.reportRevision);
  if (!revision.success) {
    throw new ReportArtifactRequestError(
      'This historical report has no immutable revision for a new export. Existing downloads remain available in Findings.',
    );
  }

  const revisionFields = {
    schemaVersion: 1 as const,
    reportId: report.id,
    reportRevisionId: revision.data.revisionId,
    ...(options.pdfBuilderVersion ? { pdfBuilderVersion: options.pdfBuilderVersion } : {}),
    ...formatFields(format, pageSize),
  };
  if (options.view) {
    if (options.pdfBuilderVersion) throw new ReportArtifactRequestError('A full-report PDF builder requires a non-summary PDF export.');
    const view = reportSummaryViewForType(report.reportType ?? 'feasibility');
    if (view !== options.view) throw new ReportArtifactRequestError('This summary view does not match the report type.');
    const builderVersion = reportSummaryBuilderVersionForView(view);
    if (options.summaryBuilderVersion && options.summaryBuilderVersion !== builderVersion) throw new ReportArtifactRequestError('Request the current summary builder.');
    const eligible = view === 'screening-summary' ? screeningSummaryEligibility(report) : reportSummaryEligibility(report);
    if (format !== 'pdf' || !eligible.ok) throw new ReportArtifactRequestError(!eligible.ok ? (eligible.reason ?? 'This saved revision cannot create a summary.') : 'Summaries are PDF exports.');
    // Generic summaries retain the exact ordinary analysis/content authority.
    const source = view === 'screening-summary' ? { ...revisionFields, sourceKind: 'report' as const }
      : createReportArtifactRequest(report, format, pageSize);
    return { ...source, view, summaryBuilderVersion: builderVersion } as ReportArtifactJobRequest;
  }
  if (options.summaryBuilderVersion) throw new ReportArtifactRequestError('A summary builder requires a summary view.');
  if (options.pdfBuilderVersion && format !== 'pdf') throw new ReportArtifactRequestError('A full-report PDF builder requires a non-summary PDF export.');
  if (options.pdfBuilderVersion && options.pdfBuilderVersion !== REPORT_PDF_BUILDER_VERSION) throw new ReportArtifactRequestError('Request the current PDF builder.');
  const reportContent = reportContentRevisionReferenceV1Schema.safeParse(
    report.reportContentRevision,
  );
  if (report.reportContentRevision != null && !reportContent.success) {
    throw new ReportArtifactRequestError(
      'The stamped report content is invalid or stale. Open a current complete Findings revision before creating an export.',
    );
  }
  if (reportContent.success) {
    const readiness = reportContent.data.readiness;
    if (
      reportContent.data.reportId !== report.id
      || reportContent.data.reportRevisionId !== revision.data.revisionId
      || reportContent.data.productId !== 'custom-brief'
      || readiness.state !== 'ready'
      || !readiness.ready
      || !readiness.current
      || !readiness.canCreateExport
    ) {
      throw new ReportArtifactRequestError(
        'This report content is stale, held, or still updating. If it is still updating, wait for the update to finish; otherwise re-run it from this report (Rerun) before creating a new export.',
      );
    }
    return {
      ...revisionFields,
      sourceKind: 'report-content',
      reportContentRevisionId: reportContent.data.contentRevisionId,
      reportContentInputHash: reportContent.data.inputHash,
      reportContentHash: reportContent.data.contentHash,
    } as ReportArtifactJobRequest;
  }
  const dossier = report as SiteRagReport & {
    analysisRevisions?: unknown;
    decisionAnalyses?: unknown;
  };
  if (dossier.analysisRevisions !== undefined || dossier.decisionAnalyses !== undefined) {
    if (!Array.isArray(dossier.analysisRevisions)
      || dossier.analysisRevisions.length === 0
      || dossier.analysisRevisions.length > 32
      || !Array.isArray(dossier.decisionAnalyses)
      || dossier.decisionAnalyses.length !== dossier.analysisRevisions.length) {
      throw new ReportArtifactRequestError('Open the complete decision dossier with one immutable snapshot per analysis revision before creating an export.');
    }
    const references = dossier.analysisRevisions.map(value => analysisRevisionReferenceV1Schema.safeParse(value));
    const snapshots = dossier.decisionAnalyses.map(value => decisionAnalysisSnapshotV1Schema.safeParse(value));
    if (references.some(value => !value.success) || snapshots.some(value => !value.success)) {
      throw new ReportArtifactRequestError('The decision dossier contains an invalid immutable analysis revision or snapshot.');
    }
    const exactReferences = references.map(value => {
      if (!value.success) throw new ReportArtifactRequestError('Invalid analysis revision.');
      return value.data;
    });
    const exactSnapshots = snapshots.map(value => {
      if (!value.success) throw new ReportArtifactRequestError('Invalid analysis snapshot.');
      return value.data;
    });
    const byId = new Map(exactSnapshots.map(snapshot => [snapshot.analysisId, snapshot]));
    if (byId.size !== exactSnapshots.length
      || new Set(exactReferences.map(reference => reference.analysisId)).size !== exactReferences.length
      || exactReferences.some(reference => {
        const snapshot = byId.get(reference.analysisId);
        const readiness = reference.readiness;
        return !snapshot || !verifyDecisionAnalysisSnapshotIdentity(snapshot).valid
          || !report.siteId || reference.siteId !== report.siteId
          || snapshot.siteId !== reference.siteId || snapshot.domain !== reference.domain
          || snapshot.inputHash !== reference.inputHash || snapshot.contentHash !== reference.contentHash
          || readiness.state !== 'ready' || !readiness.ready || !readiness.current
          || !readiness.canUseForDecision;
      })) {
      throw new ReportArtifactRequestError('The complete decision dossier must contain exact, current, ready analysis revisions and their matching immutable snapshots.');
    }
    return {
      ...revisionFields,
      sourceKind: 'decision-dossier',
      analysisRevisions: exactReferences.map(reference => ({
        analysisId: reference.analysisId,
        inputHash: reference.inputHash,
        contentHash: reference.contentHash,
      })),
    } as ReportArtifactJobRequest;
  }
  const analysis = analysisRevisionReferenceV1Schema.safeParse(report.analysisRevision);

  // A malformed or incoherent stamp cannot become a legacy report-only
  // request merely because the normal lazy-PDF bootstrap is available.
  if (report.analysisRevision != null && !analysis.success) {
    throw new ReportArtifactRequestError(
      'The stamped analysis is invalid or stale. Open a current complete analysis revision before creating an export.',
    );
  }
  if (analysis.success) {
    const bodyReadiness = analysisReadinessDecisionV1Schema.safeParse(report.analysisReadiness);
    const readiness = analysis.data.readiness;
    const exactReadiness = bodyReadiness.success
      && hashCanonicalJson(bodyReadiness.data) === hashCanonicalJson(readiness);
    if (
      !exactReadiness
      || !canExportAnalysisForPrintableReview(readiness)
    ) {
      throw new ReportArtifactRequestError(
        'This analysis is stale, held, or still updating. If it is still updating, wait for the update to finish; otherwise re-run it from this report (Rerun) before creating a new export.',
      );
    }
    return {
      ...revisionFields,
      sourceKind: 'analysis',
      analysisId: analysis.data.analysisId,
      analysisInputHash: analysis.data.inputHash,
      analysisContentHash: analysis.data.contentHash,
    } as ReportArtifactJobRequest;
  }

  // Initial downloads and retries use this exact completed readable source.
  // An absent or failed PDF never requires model/narrative regeneration.
  if (canBuildReportArtifacts(report.pdfDelivery, revision.data.deliveryDecision)) {
    return { ...revisionFields, sourceKind: 'report' } as ReportArtifactJobRequest;
  }

  if ((report.reportType as ReportTypeId | undefined) === 'financial') {
    throw new ReportArtifactRequestError(
      'Legacy illustrative Financial reports cannot create new exports. Complete the Financial model (Financial Model tab) and run a current Financial analysis.',
    );
  }
  // AUDIT-ADVISORY (2026-08-22): the persisted stamp is immutable, so the
  // policy is applied at read time — quality-held / truth-audit reasons do
  // not bar a new export request.
  const effectiveDecision = advisoryFilteredDeliveryDecision(revision.data.deliveryDecision);
  if (!effectiveDecision.canExport) {
    const reason = effectiveDecision.reasons[0]?.message;
    throw new ReportArtifactRequestError(
      reason ?? 'This report revision is not eligible for a new export.',
    );
  }
  return {
    ...revisionFields,
    sourceKind: 'report',
  } as ReportArtifactJobRequest;
}
